import React, { useEffect, useState, useCallback } from 'react';
import { useWatchlist } from '../../contexts/WatchlistContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/api';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import { isFuturesModeEnabled, getOptionsUnderlying, getFuturesLabelForUnderlying } from '../../utils/unifiedSymbolMapping';

interface AnalysisResult {
  underlying: string;
  ai_message: string;
  analysis: any;
  timestamp: string;
}

interface AIAnalysisNotificationProps {
  data?: any[];
  onNotificationClick?: (underlying: string, message: string) => void;
}

const AIAnalysisNotification: React.FC<AIAnalysisNotificationProps> = ({ data, onNotificationClick }) => {
  const { watchlist, aiAnalysisEnabled, analysisLanguage } = useWatchlist();
  useTheme();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState<Map<string, AnalysisResult>>(new Map());
  const [lastDataHash, setLastDataHash] = useState<string>('');

  const triggerAnalysis = useCallback(async () => {
    if (!token || !aiAnalysisEnabled || watchlist.length === 0) return;

    try {
      const response = await fetch(getApiUrl('/api/ai/watchlist/analysis'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          watchlist: isFuturesModeEnabled() ? watchlist.map(s => { const etf = getOptionsUnderlying(s); return etf !== s ? etf : s; }) : watchlist,
          use_accumulated: true,
          analysis_language: analysisLanguage
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.analyses) {
          const newNotifications = new Map<string, AnalysisResult>();
          
          Object.entries(result.analyses).forEach(([underlying, analysis]: [string, any]) => {
            const displayLabel = isFuturesModeEnabled() ? (getFuturesLabelForUnderlying(underlying) || underlying) : underlying;
            newNotifications.set(displayLabel, {
              underlying: displayLabel,
              ai_message: analysis.ai_message || analysis.raw_message || '',
              analysis: analysis.analysis,
              timestamp: result.timestamp
            });
          });

          setNotifications(newNotifications);
        }
      }
    } catch (error) {
      console.error('Error fetching watchlist analysis:', error);
    }
  }, [token, aiAnalysisEnabled, watchlist, analysisLanguage]);

  // Trigger analysis on initial load when watchlist has items
  useEffect(() => {
    if (token && aiAnalysisEnabled && watchlist.length > 0) {
      // Initial trigger when component mounts with valid watchlist
      const timer = setTimeout(() => {
        triggerAnalysis();
      }, 2000); // Delay to ensure data is loaded
      return () => clearTimeout(timer);
    }
  }, [token, aiAnalysisEnabled, watchlist.length > 0]); // Only re-run when watchlist becomes non-empty

  // Generate hash of data to detect changes
  useEffect(() => {
    if (!data || !aiAnalysisEnabled || watchlist.length === 0) {
      setNotifications(new Map());
      return;
    }

    // Create a more comprehensive hash that includes watchlist symbols
    const relevantData = data.filter(d => {
      const underlying = d.Underlying || d.Symbol?.split(' ')[0];
      return underlying && watchlist.includes(underlying.toUpperCase());
    });

    if (relevantData.length === 0) {
      setNotifications(new Map());
      return;
    }

    const dataHash = JSON.stringify({
      watchlist: watchlist.sort(),
      data: relevantData.slice(0, 50).map(d => ({
        Underlying: d.Underlying,
        Strike: d.Strike,
        'Delta accumelated': d['Delta accumelated'],
        'Delta Accumulated Premium': d['Delta Accumulated Premium'],
        'Bid Volume accumelated': d['Bid Volume accumelated'],
        'Ask Volume accumelated': d['Ask Volume accumelated']
      }))
    });

    // Trigger on data changes (including first change when lastDataHash is set)
    if (dataHash !== lastDataHash) {
      if (lastDataHash !== '') {
        // Data changed after initial load - trigger analysis
        triggerAnalysis();
      }
      setLastDataHash(dataHash);
    }
  }, [data, watchlist, aiAnalysisEnabled, token, triggerAnalysis, lastDataHash]);

  const handleNotificationClick = (underlying: string, message: string) => {
    if (onNotificationClick) {
      onNotificationClick(underlying, message);
    }
  };

  const handleDismiss = (underlying: string) => {
    setNotifications(prev => {
      const newMap = new Map(prev);
      newMap.delete(underlying);
      return newMap;
    });
  };

  if (!aiAnalysisEnabled || notifications.size === 0) {
    return null;
  }

  // Design System preview: MMEC.Toast look (320 px, surface-panel, gold inline-start hair, radius 4).
  const ds = isDesignSystemOn();

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: ds ? '320px' : '400px'
      }}
    >
      {Array.from(notifications.entries()).map(([underlying, notification]) => (
        <div
          key={underlying}
          onClick={() => handleNotificationClick(underlying, notification.ai_message)}
          style={{
            padding: ds ? '10px 12px' : '12px 14px',
            backgroundColor: 'var(--surface-panel, var(--elev))',
            color: 'var(--txt)',
            borderRadius: ds ? 4 : 10,
            boxShadow: 'var(--shadow-panel, var(--lift-3))',
            cursor: 'pointer',
            border: ds ? '1px solid var(--line)' : '1px solid var(--border)',
            borderInlineStart: ds ? '3px solid var(--gold)' : undefined,
            width: ds ? 320 : undefined,
            boxSizing: ds ? 'border-box' : undefined,
            fontFamily: 'var(--font-ui)',
            position: 'relative',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <strong style={{ fontSize: 13, fontWeight: 600, color: ds ? 'var(--text)' : 'var(--acc)', fontFamily: ds ? 'var(--font-mono)' : undefined }}>
              {underlying}
            </strong>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(underlying);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0 5px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          <p style={{ margin: 0, fontSize: ds ? 12 : 13, lineHeight: 1.5, color: ds ? 'var(--text-muted)' : undefined }}>
            {notification.ai_message}
          </p>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', fontFamily: ds ? 'var(--font-mono)' : undefined, direction: ds ? 'ltr' : undefined }}>
            {new Date(notification.timestamp).toLocaleTimeString()}
          </div>
        </div>
      ))}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default AIAnalysisNotification;
